import isMobile from '../../helpers/isMobile';


export default class CoreSlideshow extends HTMLElement {

    constructor() {
        super();
        this.data = { index: 0 };
        this.prev = this.prev.bind(this);
        this.next = this.next.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onFocusIn = this.onFocusIn.bind(this);
        this.onMouseEnter = this.onMouseEnter.bind(this);
        this.onMouseLeave = this.onMouseLeave.bind(this);
        this.onWheel = this.onWheel.bind(this);
        this.siteSettingsMotionChange = this.siteSettingsMotionChange.bind(this);
    }

    siteSettingsMotionChange(event) {
        this.stopped = ('off' === event.detail);
    }

    next(opts) {
        this.goTo(this.data.index + 1, opts);
    }

    prev(opts) {
        this.goTo(this.data.index - 1, opts);
    }

    goTo(index, { announce = true, moveFocus = false } = {}) {
        const max = this.$slides.length - 1;
        const nextIndex = index < 0 ? max : index > max ? 0 : index;
        if (nextIndex === this.data.index) {
            return;
        }
        this.data.index = nextIndex;
        this.updateSlides({ announce, moveFocus });
        this.classList.add('is-interacted');
    }

    autoplayStart() {
        if (!this._autoplay || this._pausedByUser || this._isPaused) return;
        this.autoplayStop();
        this.autoplayTimer = window.setTimeout(() => {
        this.next();
        this.autoplayStart();
        }, this._autoplayInterval);
    }

    autoplayStop() {
        if (this.autoplayTimer) {
            clearTimeout(this.autoplayTimer);
            this.autoplayTimer = null;
        }
    }

    setPaused(paused) {
        this._isPaused = paused;
        if (paused) {
            this.autoplayStop();
        }
        else {
            this.autoplayStart();
        }
    }

    ensureLiveRegion() {
        if (this.$live) return;
        const live = document.createElement('div');
        live.setAttribute('aria-live', 'polite');
        live.setAttribute('aria-atomic', 'true');
        live.style.position = 'absolute';
        live.style.width = '1px';
        live.style.height = '1px';
        live.style.margin = '-1px';
        live.style.border = '0';
        live.style.padding = '0';
        live.style.overflow = 'hidden';
        live.style.clip = 'rect(0 0 0 0)';
        live.style.clipPath = 'inset(50%)';
        this.appendChild(live);
        this.$live = live;
    }

    announceCurrentSlide() {
        if (!this.$live) return;
        const i = this.data.index;
        const slide = this.$slides[i];
        const label =
        slide?.getAttribute('aria-label') ||
        `Slide ${i + 1} of ${this.$slides.length}`;
        this.$live.textContent = label;
    }

    setButtonState(btn, enabled) {
        if (!btn) return;
        btn.disabled = !enabled;
        btn.setAttribute('aria-disabled', String(!enabled));
    }

    setSlideFocusable(slide, isActive) {
        // Make the slide itself focusable (for programmatic focus)
        slide.tabIndex = isActive ? 0 : -1;
        // Hide non-active slides from screen readers.
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

        // prevent tabbing into inactive slide content
        if ('inert' in slide) {
            slide.inert = !isActive;
        } 
        else {
        // Fallback: set tabindex=-1 on focusable descendants
        const focusables = slide.querySelectorAll(
            'a, button, input, select, textarea, [tabindex]'
        );
        focusables.forEach((el) => {
            if (!el.hasAttribute('data-orig-tabindex')) {
            const orig = el.getAttribute('tabindex');
            el.setAttribute('data-orig-tabindex', orig === null ? '' : orig);
            }
            if (!isActive) el.setAttribute('tabindex', '-1');
            else {
            const orig = el.getAttribute('data-orig-tabindex');
            if (orig === '') el.removeAttribute('tabindex');
            else el.setAttribute('tabindex', orig);
            }
        });
        }
    }

    updateSlides({ announce = false, moveFocus = false } = {}) {

        if (isMobile() && this.hasAttribute('desktop-only')) {
            this.$slides.forEach((el, i) => {
                el.style.pointerEvents =  'auto';
                el.removeAttribute('aria-hidden');
                el.inert = false;
            });
            return;
        }

        const total = this.$slides.length;

        this.$slides.forEach((el, i) => {
            const isActive = i === this.data.index;

            el.classList[isActive ? 'add' : 'remove']('is-active');
            el.setAttribute('role', 'group');
            el.setAttribute('aria-roledescription', 'slide');
            el.setAttribute('aria-label', `${i + 1} of ${total}`);

            this.setSlideFocusable(el, isActive);

            // Titles that delegate control to the slideshow (auto-animate="false") reveal on the
            // active slide and animate back out otherwise — so each title animates in and out.
            el.querySelectorAll('split-text[auto-animate="false"]').forEach((st) => {
                st.classList.toggle('in-view', isActive);
            });

            if (this._transform) {
                if (this._vertical) {
                    // Only the background layer moves between slides; the text and label
                    // stay anchored and simply animate in/out. The active background scales
                    // up to 100%; neighbours sit slightly smaller and scale in as they arrive.
                    const offset = i - this.data.index;
                    const bg = el.querySelector('[data-bg]');
                    if (bg) {
                        const scale = offset === 0 ? 1 : 0.7;
                        bg.style.transform = `translateY(${offset * 100}%) scale(${scale})`;
                    }
                } else {
                    const margin = (i - this.data.index) * (26 * window.innerWidth / 1728);
                    const shift =
                        i <= this.data.index
                        ? (i - this.data.index) * 100
                        : ((i - 1 - this.data.index) * 23.23 + 100);
                    const scale = i <= this.data.index ? 1 : 0.2323;
                    el.style.transform = `translateX(${shift}%) translateX(${margin}px) scale(${scale})`;
                }
            }


            el.style.pointerEvents = isActive ? 'auto' : 'none';

            if (this.stopped) {
                el.style.transition = 'none';
            }
            else {
                el.style.removeProperty("transition");
            }
            
        });

        const atEnd = this.data.index === this.$slides.length - 1;
        const atStart = this.data.index === 0;
        if (this.hasAttribute('loop')) {
            this.setButtonState(this.$nextBtn, true);
            this.setButtonState(this.$prevBtn, true);
        }
        else {
            this.setButtonState(this.$nextBtn, !atEnd);
            this.setButtonState(this.$prevBtn, !atStart);
        }

        if (announce) {
            this.announceCurrentSlide();
        }

        if (moveFocus) {
            const active = this.$slides[this.data.index];
            active?.focus({ preventScroll: true });
        }
    }

    onKeyDown(e) {
        if (!this.contains(document.activeElement)) return;
        switch (e.key) {
            case 'ArrowLeft':
            e.preventDefault();
            this.prev({ announce: true, moveFocus: true });
            break;
            case 'ArrowRight':
            e.preventDefault();
            this.next({ announce: true, moveFocus: true });
            break;
            case 'Home':
            e.preventDefault();
            this.goTo(0, { announce: true, moveFocus: true });
            break;
            case 'End':
            e.preventDefault();
            this.goTo(this.$slides.length - 1, { announce: true, moveFocus: true });
            break;
        }
    }

    onFocusIn() {
        this.setPaused(true);
    }

    onMouseEnter() {
        this.setPaused(true);
    }

    onMouseLeave() {
        this.setPaused(false);
    }

    // Opt-in "stepped scroll": advance one slide per wheel gesture (enable with the `scroll-step` attribute).
    onWheel(e) {

        if (isMobile()) return;

        e.preventDefault();

        const delta = e.deltaY;
        if (Math.abs(delta) < 8) return;

        const atEnd = this.data.index === this.$slides.length - 1;
        const atStart = this.data.index === 0;

        // // Let the page scroll naturally once the slideshow is at its first/last slide,
        // // so visitors can still reach the content below.
        // if (!this._wheelLock && ((delta > 0 && atEnd) || (delta < 0 && atStart))) {
        //     return;
        // }

        e.preventDefault();

        // Swallow trackpad momentum while a step transition is in progress.
        if (this._wheelLock) return;

        this._wheelLock = true;
        if (delta > 0) {
            this.next({ announce: true });
        } else {
            this.prev({ announce: true });
        }
        window.setTimeout(() => { this._wheelLock = false; }, 800);
    }

    connectedCallback() {

        if (isMobile() && this.hasAttribute('desktop-only')) {
            return;
        }

        document.addEventListener('site_settings_motion', this.siteSettingsMotionChange, false);

        this.setAttribute('role', 'region');
        this.setAttribute('aria-roledescription', 'carousel');

        if (!this.hasAttribute('aria-label')) {
            this.setAttribute('aria-label', 'Slideshow');
        }

        this.ensureLiveRegion();

        this._autoplayInterval = Number(this.getAttribute('interval')) || 3000;

        // Autoplay default true unless autoplay="false"
        this._autoplay = this.getAttribute('autoplay') !== 'false';

        this._transform = this.getAttribute('transform') !== 'false';
        this._vertical = this.getAttribute('direction') === 'vertical';

        this._pausedByUser = false;
        this._isPaused = false;

        // Respect reduced motion: disable autoplay
        const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        if (prefersReduced) this._autoplay = false;

        this.$slides = this.querySelectorAll('[data-slide]');
        this.$prevBtn = this.querySelector('[data-prev-btn]');
        this.$nextBtn = this.querySelector('[data-next-btn]');

        this.$prevBtn?.addEventListener('click', (e) => {
            e.preventDefault();
            this.prev({ announce: true, moveFocus: false });
        });

        this.$nextBtn?.addEventListener('click', (e) => {
            e.preventDefault();
            this.next({ announce: true, moveFocus: false });
        });

        this.addEventListener('keydown', this.onKeyDown);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('mouseenter', this.onMouseEnter);
        this.addEventListener('mouseleave', this.onMouseLeave);

        // Opt-in stepped-scroll navigation.
        this._scrollStep = this.hasAttribute('scroll-step') && this.getAttribute('scroll-step') !== 'false';
        if (this._scrollStep) {
            this.addEventListener('wheel', this.onWheel, { passive: false });
        }

        this.updateSlides({ announce: false, moveFocus: false });

        if (this._autoplay) this.autoplayStart();
    }

    disconnectedCallback() {

        if (isMobile() && this.hasAttribute('desktop-only')) {
            return;
        }

        this.autoplayStop();
        this.removeEventListener('keydown', this.onKeyDown);
        this.removeEventListener('focusin', this.onFocusIn);
        this.removeEventListener('mouseenter', this.onMouseEnter);
        this.removeEventListener('mouseleave', this.onMouseLeave);
        if (this._scrollStep) {
            this.removeEventListener('wheel', this.onWheel);
        }
    }

}

customElements.define('core-slideshow', CoreSlideshow);